import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles, Theme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { toCurrencyDisplayFormat } from '../../../util/format';
import PGEButton from '../../buttons';
import colors from '../../../themes/main-colors';
import { useTranslation } from '../../../hooks/useTranslation';

interface PaySelectedCardProps {
  totalPaymentAmount: number;
  accountsSelected: number;
  areAllWithBalanceSelected: boolean;
  onSelectAllChange: (checked: boolean) => void;
  onPaySelected?: () => void;
}

interface StyleProps {
  isAccountSelected: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  root: {
    color: colors.noirBlur,
    borderRadius: 4,
    overflow: 'hidden',
    boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.25)',
    backgroundColor: props =>
      `${props.isAccountSelected ? colors.linenWhite : colors.white}`,

    // Large desktop screens
    '@media (min-width:1536px)': {
      width: 300,
    },
  },
  header: {
    backgroundColor: props =>
      `${props.isAccountSelected ? colors.sparkBlue : colors.noirBlur}`,
    color: colors.white,
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 18,
    padding: theme.spacing(1.5, 2),
  },
  content: {
    padding: theme.spacing(3, 3, 2.5),
    display: 'flex',
    paddingBottom: '8px',
    justifyContent: 'space-between',
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
    // Large desktop screens
    '@media (min-width:1536px)': {
      flexDirection: 'column',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  amountBox: {
    border: `1px solid ${colors.grey3}`,
    borderRadius: 2,
    backgroundColor: colors.white,
    textAlign: 'center',
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },
  amountLabel: {
    fontSize: 16,
    marginBottom: theme.spacing(1),
  },
  amountValue: {
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: 0.2,
  },
  checkboxRow: {
    alignItems: 'flex-start',
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(1),
    // Large desktop screens
    '@media (min-width:1536px)': {
      marginBottom: theme.spacing(2.5),
    },
  },
  checkbox: {
    padding: 0,
    marginRight: theme.spacing(1.5),
    color: colors.sparkBlue,
    '&.Mui-checked': {
      color: colors.sparkBlue,
    },
    '&.MuiCheckbox-indeterminate': {
      color: colors.sparkBlue,
    },
  },
  checkboxLabel: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.25,
  },
  payButton: {
    backgroundColor: colors.orange,
    color: colors.white,
    textTransform: 'none',
    fontSize: 18,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#e65100',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.grey3,
      color: colors.white,
    },
  },
  cancelButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 18,
    color: colors.sparkBlue,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
}));

const PaySelectedAccountsCard: React.FC<PaySelectedCardProps> = ({
  totalPaymentAmount = 0,
  accountsSelected,
  areAllWithBalanceSelected,
  onSelectAllChange,
  onPaySelected,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ isAccountSelected: accountsSelected > 0 });

  const partiallySelected = !areAllWithBalanceSelected && accountsSelected > 0;

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (onSelectAllChange) {
      onSelectAllChange(checked);
    }
  };

  const onCancel = () => {
    onSelectAllChange(false);
  };

  return (
    <Card className={classes.root}>
      <Box className={classes.header}>
        <Typography component="div">
          {accountsSelected} {t('ACCOUNTS_SELECTED')}
        </Typography>
      </Box>

      <CardContent className={classes.content}>
        <Box>
          {accountsSelected === 0 && (
            <Typography style={{ marginBottom: '16px' }} variant="body2">
              {t('SELECT_ACCOUNTS_TO_PAY')}
            </Typography>
          )}
          {accountsSelected > 0 && (
            <Box className={classes.amountBox}>
              <Typography className={classes.amountLabel}>
                {t('TOTAL_PAYMENT_AMOUNT')}:
              </Typography>
              <Typography className={classes.amountValue}>
                ${toCurrencyDisplayFormat(totalPaymentAmount, true, 'CR')}
              </Typography>
            </Box>
          )}

          <FormControlLabel
            className={classes.checkboxRow}
            control={
              <Checkbox
                className={classes.checkbox}
                checked={areAllWithBalanceSelected}
                indeterminate={partiallySelected}
                onChange={handleCheckboxChange}
              />
            }
            label={
              <Typography className={classes.checkboxLabel}>
                {t('SELECT_ALL_WITH_AMOUNT_DUE')}
              </Typography>
            }
          />
        </Box>
        <Box>
          <PGEButton
            fullWidth
            variant="contained"
            className={classes.payButton}
            onClick={onPaySelected}
            disabled={accountsSelected === 0}
          >
            {t('PAY_SELECTED')}
          </PGEButton>

          {accountsSelected > 0 && (
            <Box display="flex" justifyContent="center">
              <Button className={classes.cancelButton} onClick={onCancel}>
                {t('CANCEL')}
              </Button>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaySelectedAccountsCard;
